/**
 * LPDG AI Chat — multi-site embed
 *
 * Example (dealer site):
 *
 * <script
 *   async
 *   src="https://www.leprixdugros.com/app/themes/lpdg-2022/chat-embed.js"
 *   data-concession-id="387"
 *   data-agent-name="Julie"
 *   data-agent-image="https://…/agent.jpg"
 *   data-logo="https://…/logo.svg"
 *   data-inventory-url-new="https://www.example.com/neufs/"
 *   data-inventory-url-used="https://www.example.com/occasion/"
 * ></script>
 *
 * Requires THEME_CHAT=1 + OPENAI_API_KEY on the LPDG host that serves the API.
 */
import chatCss from './chat-embed.css';
import { Chat } from './components/Chat.js';

function findEmbedScript() {
  if (document.currentScript && document.currentScript.src) {
    return document.currentScript;
  }
  const scripts = document.querySelectorAll('script[src*="chat-embed"]');
  return scripts[scripts.length - 1] || null;
}

function readConfig(script) {
  const ds = script?.dataset || {};
  const apiBase = script?.src
    ? new URL(script.src, window.location.href).origin
    : window.location.origin;

  return {
    apiBase,
    concessionId: String(ds.concessionId || '').trim(),
    agentName: String(ds.agentName || 'Julie').trim() || 'Julie',
    agentImage: String(ds.agentImage || '').trim(),
    logo: String(ds.logo || '').trim(),
    inventoryUrlNew: String(ds.inventoryUrlNew || '').trim(),
    inventoryUrlUsed: String(ds.inventoryUrlUsed || '').trim(),
  };
}

function injectCss(cssText) {
  if (document.getElementById('lpdg-chat-embed-css')) return;
  const style = document.createElement('style');
  style.id = 'lpdg-chat-embed-css';
  style.textContent = cssText;
  document.head.appendChild(style);
}

function escapeHtml(text) {
  const div = document.createElement('div');
  div.textContent = String(text ?? '');
  return div.innerHTML;
}

function buildMarkup(config) {
  const agentImage = config.agentImage || `${config.apiBase}/app/themes/lpdg-2022/images/chat.jpg`;
  const logoHtml = config.logo
    ? `<img class="c-chat_logo_img" src="${escapeHtml(config.logo)}" alt="">`
    : `<span class="c-chat_logo_text">${escapeHtml(config.agentName)}</span>`;

  const wrap = document.createElement('div');
  wrap.innerHTML = `
    <c-chat
      class="c-chat is-embed"
      data-mode="embed"
      data-api-base="${escapeHtml(config.apiBase)}"
      data-agent-name="${escapeHtml(config.agentName)}"
      data-selected-concession="${escapeHtml(config.concessionId)}"
      data-inventory-url-new="${escapeHtml(config.inventoryUrlNew)}"
      data-inventory-url-used="${escapeHtml(config.inventoryUrlUsed)}"
      data-disable-vehicle-search="1"
      data-hide-concession-select="1"
    >
      <button class="c-chat_button" type="button">
        <div class="c-chat_button_avatar">
          <img src="${escapeHtml(agentImage)}" alt="Chat">
        </div>
        <span class="c-chat_button_label">Des questions? Je peux vous aider.</span>
      </button>
      <div class="c-chat_content is-not-active">
        <div class="c-chat_content_inner">
          <div class="c-chat_content_header">
            <div class="c-chat_logo">${logoHtml}</div>
            <div class="c-chat_toolbar">
              <button class="c-chat_toolbar_reset" type="button" aria-label="Réinitialiser">
                <svg class="c-chat_toolbar_icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12C22 17.5228 17.5229 22 12 22C6.4772 22 2 17.5228 2 12C2 6.47715 6.4772 2 12 2V4C7.5817 4 4 7.58172 4 12C4 16.4183 7.5817 20 12 20C16.4183 20 20 16.4183 20 12C20 9.25022 18.6127 6.82447 16.4998 5.38451L16.5 8H14.5V2L20.5 2V4L18.0008 3.99989C20.4293 5.82434 22 8.72873 22 12Z"></path></svg>
              </button>
              <button class="c-chat_toolbar_close" type="button" aria-label="Fermer">
                <svg class="c-chat_toolbar_icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M11.9997 10.5865L16.9495 5.63672L18.3637 7.05093L13.4139 12.0007L18.3637 16.9504L16.9495 18.3646L11.9997 13.4149L7.04996 18.3646L5.63574 16.9504L10.5855 12.0007L5.63574 7.05093L7.04996 5.63672L11.9997 10.5865Z"></path></svg>
              </button>
            </div>
          </div>
          <div class="c-chat_concession" hidden>
            <select class="c-chat_concession_select">
              <option value="${escapeHtml(config.concessionId)}" selected>${escapeHtml(config.concessionId)}</option>
            </select>
          </div>
          <div class="c-chat_content_body">
            <div class="c-chat_agent">
              <div class="c-chat_agent_avatar">
                <img src="${escapeHtml(agentImage)}" alt="">
              </div>
              <div class="c-chat_agent_name">${escapeHtml(config.agentName)}</div>
              <div class="c-chat_agent_disclaimer">
                Cet assistant IA est fourni à titre informatif.<br>N’hésitez pas à valider avec un conseiller.
              </div>
            </div>
            <div class="c-chat_messages_container"></div>
          </div>
          <div class="c-chat_user_input">
            <input type="text" class="c-chat_user_input_field" placeholder="Écrivez votre message...">
            <button class="c-chat_user_input_button" type="button" aria-label="Envoyer">
              <svg class="c-chat_send_icon" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M13.0001 7.82843V20H11.0001V7.82843L5.63614 13.1924L4.22192 11.7782L12.0001 4L19.7783 11.7782L18.3641 13.1924L13.0001 7.82843Z"></path></svg>
            </button>
          </div>
        </div>
      </div>
    </c-chat>
  `;

  const el = wrap.firstElementChild;
  // setAttribute avoids HTML attribute truncation (escapeHtml does not escape quotes).
  el.setAttribute(
    'data-concessions',
    JSON.stringify(
      config.concessionId
        ? [{ id: Number(config.concessionId) || config.concessionId, title: config.agentName }]
        : []
    )
  );
  return el;
}

function boot() {
  const script = findEmbedScript();
  const config = readConfig(script);

  if (!config.concessionId) {
    console.warn('[LpdgChat] data-concession-id is required');
    return;
  }

  injectCss(typeof chatCss === 'string' ? chatCss : String(chatCss || ''));

  if (!customElements.get('c-chat')) {
    customElements.define('c-chat', Chat);
  }

  if (document.querySelector('c-chat.is-embed')) {
    return;
  }

  const el = buildMarkup(config);
  document.body.appendChild(el);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
